/**
 * The small thread pieces other screens show (2026-10-29): the bar under a
 * message that started a thread ("Name · 4 messages · last reply 2 min
 * ago"), the "started a thread" system message, the top of a thread (its
 * name and the message it came from), and the notes over a thread's
 * composer when it is archived or locked.
 */
import {
  ChannelType,
  Routes,
  snowflakeDate,
  type Channel,
  type Message,
} from '@neosocial/protocol';
import { useEffect, useState } from 'react';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { useFlag } from '../store/config';
import { useData, useDataShallow } from '../store/data';
import { userName } from '../store/selectors';
import { useUi } from '../store/ui';
import { Icon } from '../ui/kit';
import { openProfile } from '../hub/ProfileCard';
import { PreviewRow } from '../hub/Search';
import { openThread, openThreadList, setArchived, threadPerms } from './actions';

/** The thread a message started: the live copy in the store, else the one the message carries. */
export function useMessageThread(m: Message): Channel | null {
  return useData((d) => {
    const c = d.channels[m.id];
    if (c && c.parent_id === m.channel_id) return c;
    return m.thread ?? null;
  });
}

/** Under a message that started a thread: its name, how many messages, the last reply. */
export function ThreadBar({ m }: { m: Message }) {
  const { t, fmt } = useT();
  const th = useMessageThread(m);
  // the console can turn threads off: the bar goes (the server refuses them too)
  const on = useFlag('threads');
  if (!th || !on) return null;
  const n = th.message_count ?? 0;
  const last =
    th.last_message_id && n > 0
      ? t('th.bar.last', { time: fmt.rel(snowflakeDate(th.last_message_id)) })
      : null;
  return (
    <button
      type="button"
      className="ns-th-bar"
      data-archived={th.thread_metadata?.archived || undefined}
      aria-label={t('th.bar.open', { name: th.name ?? '' })}
      onClick={(e) => {
        e.stopPropagation();
        openThread(th);
      }}
    >
      <span className="ns-th-bar-i" aria-hidden="true">
        <Icon name="thread" size="s" />
      </span>
      <b className="ns-th-bar-n" dir="auto">
        {th.name}
      </b>
      <span className="ns-th-bar-c nx-num">
        {n > 0 ? t('th.bar.count', { n }) : t('th.bar.none')}
      </span>
      {last ? <span className="ns-th-bar-l">{last}</span> : null}
      {th.thread_metadata?.archived ? (
        <span className="ns-th-chip">{t('th.row.archived')}</span>
      ) : null}
      <Icon name="chev-right" size="s" cls="ns-th-bar-go" />
    </button>
  );
}

/** "{author} started a thread: {name}. See all threads" (Discord's THREAD_CREATED). */
export function ThreadCreatedRow({ m }: { m: Message }) {
  const { t, fmt } = useT();
  const gid = m.guild_id ?? null;
  const name = useData((d) => userName(d, m.author.id, gid));
  const threadId = m.message_reference?.channel_id;
  const thread = useData((d) => (threadId ? d.channels[threadId] : undefined));
  return (
    <article className="msg sys ns-th-sys" data-mid={m.id} id={`msg-${m.id}`} tabIndex={-1}>
      <div className="msg-gut">
        <span className="sys-ic">
          <Icon name="thread" size="s" />
        </span>
      </div>
      <div className="msg-body">
        <p className="sys-t">
          <span>
            <button
              type="button"
              className="sys-name"
              onClick={(e) => openProfile(m.author.id, e.currentTarget)}
            >
              <bdi>{name}</bdi>
            </button>{' '}
            {t('th.sys.started')}{' '}
            {thread ? (
              <button type="button" className="ns-link" onClick={() => openThread(thread)}>
                <bdi>{m.content}</bdi>
              </button>
            ) : (
              <b>
                <bdi>{m.content}</bdi>
              </b>
            )}
            {'. '}
            <button type="button" className="ns-link" onClick={() => openThreadList(m.channel_id)}>
              {t('th.sys.seeAll')}
            </button>
          </span>{' '}
          <time className="msg-time" dateTime={m.timestamp}>
            {fmt.stamp(m.timestamp)}
          </time>
        </p>
      </div>
    </article>
  );
}

/**
 * The top of a thread's history: its name, who started it, and the message
 * it came from (a thread has its message's ID), as Discord shows it.
 */
export function ThreadStart({ thread }: { thread: Channel }) {
  const { t } = useT();
  const parentId = thread.parent_id ?? '';
  const owner = useData((d) =>
    thread.owner_id ? userName(d, thread.owner_id, thread.guild_id ?? null) : '',
  );
  const held = useData((d) => d.messages[parentId]?.byId[thread.id]);
  const [starter, setStarter] = useState<Message | null | undefined>(held);
  // only a public thread can come from a message (it has the message's ID); a 404 means it didn't
  const fromMessage = thread.type === ChannelType.PUBLIC_THREAD;
  useEffect(() => {
    if (held || !parentId || !fromMessage) return;
    let live = true;
    api.client
      .get<Message>(Routes.channelMessage(parentId, thread.id))
      .then((m) => live && setStarter(m))
      .catch(() => live && setStarter(null));
    return () => {
      live = false;
    };
  }, [held, parentId, thread.id, fromMessage]);
  const msg = held ?? starter;
  return (
    <div className="ch-start ns-th-start">
      <div className="ch-start-ic">
        <Icon name="thread" size="xl" />
      </div>
      <h2 dir="auto">{thread.name}</h2>
      {owner ? <p>{t('th.start.by', { name: owner })}</p> : null}
      {msg ? (
        <div className="ns-th-orig">
          <span className="ns-th-orig-h">{t('th.start.original')}</span>
          <ul>
            <PreviewRow
              m={msg}
              cls="ns-th-orig-row"
              close={() => undefined}
              onOpen={() =>
                useUi.getState().set({ jump: { channelId: parentId, messageId: msg.id } })
              }
            />
          </ul>
        </div>
      ) : (
        <p>{t('th.start.sub')}</p>
      )}
    </div>
  );
}

/** Over an archived thread's composer: sending brings it back (Discord); a moderator may unarchive. */
export function ThreadArchivedNote({ thread }: { thread: Channel }) {
  const { t } = useT();
  const p = useDataShallow((d) => threadPerms(d, thread));
  const meId = useData((d) => d.me?.id);
  const mayUnarchive =
    (p.manage || (!!meId && thread.owner_id === meId)) &&
    (!thread.thread_metadata?.locked || p.manage);
  if (!thread.thread_metadata?.archived) return null;
  return (
    <div className="cmp-bar ns-th-note" role="status">
      <Icon name="history" size="s" />
      <span>{t(p.send ? 'th.archived.note' : 'th.archived.noteMod')}</span>
      {mayUnarchive ? (
        <button type="button" onClick={() => void setArchived(thread, false)}>
          {t('th.archived.unarchive')}
        </button>
      ) : null}
    </div>
  );
}

/** In place of a locked thread's composer, for those who may not write in it. */
export function ThreadLockedNote() {
  const { t } = useT();
  return (
    <div className="cmp-wrap">
      <div className="cmp ns-cmp-locked ns-th-locked" role="note">
        <Icon name="lock" size="s" />
        <span>{t('th.locked.note')}</span>
      </div>
    </div>
  );
}
